import GlobalModals from '@components/modals/GlobalModals';
import Navbar from '@components/navbar/Navbar';
import { questPromptsFn } from '@devHubSingleQuest/store/devhubSingleQuestStore.variables';
import useAddQuestPrompt from '@devHubSingleQuest/store/hooks/useAddQuestPrompt';
import Address from '@models/Address';
import { EToastVariant } from '@notifications/components/Toast/Toast.enum';
import Toasts from '@notifications/components/Toasts';
import { ECookie } from '@settings/features/settingsOverview/features/settingsOverviewCookies/hooks/useCookies/useCookies.enums';
import {
	deleteGoogleAnalyticsCookies,
	deleteMouseflowCookies
} from '@settings/features/settingsOverview/features/settingsOverviewCookies/hooks/useCookies/useCookies.functions';
import SocketStore from '@store/socketStore/SocketStore';
import useAddToast from '@store/toastsStore/hooks/useAddToast';
import UserStore from '@store/userStore/UserStore';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { Hydrate, QueryCache, QueryClient, QueryClientConfig } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { QUERY_CACHE_KEY } from '@utils/variables';
import { Provider as JotaiProvider } from 'jotai';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ResponsiveProvider from 'src/contexts/ResponsiveContext';
import { getCookie, removeCookie } from 'src/functions/cookies';
import { isToastNotificationsActivated, isWebSocketActivated } from 'src/functions/featureFlag';
import { isProdEnv } from 'src/functions/functions';
import { StoreDebug } from 'src/store/utils/StoreDebug/StoreDebug';
import Web3Store from 'src/store/web3Store/Web3Store';
import GlobalStyles from 'src/styles/theme/GlobalStyles';
import { darkTheme } from 'src/styles/theme/theme';
import { ThemeProvider } from 'styled-components';

type TProviders = Pick<AppProps, 'pageProps'> & { children: ReactNode };

export const globalQueryConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5,
			cacheTime: 1000 * 60 * 60 * 24 * 7
		}
	}
};

const persistOptions = {
	persister: createSyncStoragePersister({
		storage: typeof document !== 'undefined' ? window.localStorage : undefined,
		key: QUERY_CACHE_KEY,
		throttleTime: 1000
	}),
	maxAge: 1000 * 60 * 60 * 24 * 7
};

const pageTransitionTimeout = {
	enter: 500,
	exit: 500
};

const isProduction = process.env.BUILD_ENV === 'production';

const Providers = ({ pageProps, children }: TProviders) => {
	const { addToast } = useAddToast();
	const { addQuestPrompt } = useAddQuestPrompt();

	const [queryClient] = useState(() => {
		const queryCache = new QueryCache({
			onError: (error, query) => {
				if (query?.meta?.errorToast) {
					addToast({ ...query.meta.errorToast, variant: EToastVariant.error, rawError: error });
				}
			},
			onSuccess: (data, query) => {
				if (query?.meta?.questPrompt) {
					const _data = data as { address: string | null };
					const { contractName } = query?.meta?.questPrompt as { contractName: string | null };
					const deployedAddress = _data?.address ? Address.from(_data.address) : null;

					if (deployedAddress && contractName) {
						addQuestPrompt(questPromptsFn.deployingContractSuccess(contractName, deployedAddress));
					}
				}
			}
		});

		const queryClient = new QueryClient({
			...globalQueryConfig,
			queryCache
		});

		return queryClient;
	});

	broadcastQueryClient({
		queryClient,
		broadcastChannel: 'ng-app'
	});

	return (
		<PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
			<Hydrate state={pageProps.dehydratedState}>
				<ResponsiveProvider>
					<ThemeProvider theme={darkTheme}>
						<GlobalStyles />
						<GlobalModals />
						<Navbar />

						{isToastNotificationsActivated() ? <Toasts /> : null}

						{/* ========= Store ========== */}
						{!isProdEnv() ? <StoreDebug /> : null}
						<Web3Store />
						<UserStore />
						{isWebSocketActivated() ? <SocketStore /> : null}
						{/* ======= End Store ======== */}

						{children}
					</ThemeProvider>
				</ResponsiveProvider>
				<ReactQueryDevtools initialIsOpen={false} /> {/* help visualize all of the inner workings of React Query */}
			</Hydrate>
		</PersistQueryClientProvider>
	);
};

const NodeGuardiansApp = ({ Component, pageProps }: AppProps) => {
	const router = useRouter();

	const providers = useMemo(
		() => (
			<Providers pageProps={pageProps}>
				<TransitionGroup component={null}>
					<CSSTransition
						key={pageProps.customPathname || router.pathname}
						timeout={pageTransitionTimeout}
						classNames={`switch-page`}
					>
						<Component {...pageProps} />
					</CSSTransition>
					<span id='bl' />
				</TransitionGroup>
			</Providers>
		),
		[Component, pageProps, router.pathname]
	);

	useEffect(() => {
		document.body.className = pageProps.isSingleQuestPage ? 'single-quest-page' : '';
	}, [pageProps?.isSingleQuestPage]);

	useEffect(() => {
		if ('ontouchstart' in window) {
			document.addEventListener('contextmenu', (e) => {
				e.preventDefault();
				return false;
			});
		}
	}, []);

	useEffect(() => {
		const analyticsConsent = !!getCookie(ECookie.analytics);
		const mouseflowConsent = !!getCookie(ECookie.mouseflow);
		const isReloadNeeded = getCookie(ECookie.isReloadNeeded);

		if (isReloadNeeded) removeCookie(ECookie.isReloadNeeded);
		if (!analyticsConsent) deleteGoogleAnalyticsCookies();
		if (!mouseflowConsent) deleteMouseflowCookies();
	}, []);

	return (
		<>
			<Head>
				<meta
					name='viewport'
					content='minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover'
				/>

				{isProduction ? (
					<link rel='shortcut icon' href='/favicons/favicon.svg' />
				) : (
					<link rel='shortcut icon' href='/favicons/favicon-staging.svg' />
				)}
			</Head>
			{isProduction ? (
				<>
					{!!getCookie('mouseflowConsent') ? (
						<Script type='text/javascript' id='script-mouseflow' strategy='lazyOnload'>
							{`window._mfq = window._mfq || [];
								(function() {
									var mf = document.createElement("script");
									mf.type = "text/javascript"; mf.defer = true;
									mf.src = "//cdn.mouseflow.com/projects/4b0febac-6571-42be-9393-3971ff19f48c.js";
									document.getElementsByTagName("head")[0].appendChild(mf);
								})();`}
						</Script>
					) : null}

					<Script type='text/javascript' id='script-google-analytics-default' strategy='lazyOnload'>
						{`window.dataLayer = window.dataLayer || [];
							function gtag(){dataLayer.push(arguments);}

							gtag('consent', 'default', {
								ad_storage: 'denied',
								analytics_storage: 'denied'
							});`}
					</Script>

					<Script
						strategy='lazyOnload'
						id='script-google-analytics'
						src='https://www.googletagmanager.com/gtag/js?id=G-P0PSVWRK24'
					/>

					<Script type='text/javascript' id='script-google-analytics-config' strategy='lazyOnload'>
						{`gtag('js', new Date());
							gtag('config', 'G-P0PSVWRK24');`}
					</Script>

					<Script strategy='afterInteractive' id='script-gtag'>
						{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
										new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
										j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
										'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
										})(window,document,'script','dataLayer','GTM-KTLSJQQ');
									`}
					</Script>

					<Script type='text/javascript' id='script-gtag-footer' strategy='lazyOnload'>
						{`
								<!-- Google Tag Manager (noscript) -->
								<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTLSJQQ"
								height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
								<!-- End Google Tag Manager (noscript) -->
								`}
					</Script>

					{!!getCookie('analyticsConsent') ? (
						<Script type='text/javascript' id='script-google-analytics-update' strategy='lazyOnload'>
							{`gtag('consent', 'update', {
									ad_storage: 'granted',
									analytics_storage: 'granted'
								});`}
						</Script>
					) : null}
				</>
			) : null}

			<JotaiProvider>{providers}</JotaiProvider>
		</>
	);
};

export default NodeGuardiansApp;
